/**
 * Data Operations card for the Volume + OBV Breakout Scanner (Investment
 * Ideas -- screening only). Lets an admin (1) see whether the price data the
 * scanner needs exists for the latest completed session, (2) run the scanner
 * for that session, and (3) backfill a date range -- all as background jobs
 * with live progress, plus a durable "last run" that survives restarts and
 * shows even a run that found zero signals.
 *
 * Talks only to the ADMIN-only `/volume-obv-breakout/admin/*` routes; never
 * triggers a price import itself (those are the cards above on this page).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HistoryIcon from "@mui/icons-material/History";
import volumeObvBreakoutService from "@/services/volumeObvBreakoutService";
import type { VolumeObvReadinessResponse, VolumeObvScanJobStatus, VolumeObvScanStatusResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

const CHIP_COLOR: Record<string, "success" | "warning" | "error" | "default" | "info"> = {
  SUCCESS: "success",
  COMPLETED: "success",
  PARTIAL: "warning",
  FAILED: "error",
  RUNNING: "info",
};

function dateLabel(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function dateTimeLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function countsLabel(counts: Record<string, number> | null | undefined): string {
  const entries = Object.entries(counts ?? {});
  if (entries.length === 0) return "none";
  return entries.map(([k, v]) => `${k.replace(/_/g, " ").toLowerCase()}: ${v}`).join(", ");
}

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight={700}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export function VolumeObvScannerOperationsCard() {
  const [readiness, setReadiness] = useState<VolumeObvReadinessResponse | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(true);
  const [status, setStatus] = useState<VolumeObvScanStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<"latest" | "range" | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const prevRunningRef = useRef(false);

  const loadReadiness = useCallback(async () => {
    setReadinessLoading(true);
    try {
      const data = await volumeObvBreakoutService.getReadiness();
      setReadiness(data);
      // Sensible backfill defaults: the last ~10 calendar days up to the latest completed session.
      setToDate((prev) => prev || data.latest_completed_session);
      setFromDate((prev) => {
        if (prev) return prev;
        const end = new Date(`${data.latest_completed_session}T00:00:00`);
        end.setDate(end.getDate() - 10);
        return end.toISOString().slice(0, 10);
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setReadinessLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await volumeObvBreakoutService.getScanStatus());
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    void loadReadiness();
    void loadStatus();
  }, [loadReadiness, loadStatus]);

  const job: VolumeObvScanJobStatus | null | undefined = status?.job;
  const running = job?.state === "RUNNING" || Boolean(status?.scan_running);

  // Poll while a scan is in flight; refresh readiness once it finishes so the
  // "latest scanned session" / counts reflect the new results.
  useEffect(() => {
    if (!running) {
      if (prevRunningRef.current) void loadReadiness();
      prevRunningRef.current = false;
      return;
    }
    prevRunningRef.current = true;
    const timer = setInterval(() => void loadStatus(), 3000);
    return () => clearInterval(timer);
  }, [running, loadStatus, loadReadiness]);

  const start = async (kind: "latest" | "range") => {
    setStarting(kind);
    setError(null);
    try {
      const body = kind === "latest" ? {} : { start_date: fromDate, end_date: toDate };
      await volumeObvBreakoutService.startScan(body);
      await loadStatus();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setStarting(null);
    }
  };

  const lastRun = status?.last_run;
  const lastRunWhen = dateTimeLabel(lastRun?.completed_at ?? lastRun?.started_at);
  const lastSummary = (lastRun?.summary ?? {}) as Record<string, unknown>;
  const progress = job && job.total_stocks > 0 ? Math.round((job.processed_stocks / job.total_stocks) * 100) : 0;
  const rangeInvalid = !fromDate || !toDate || fromDate > toDate;

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mb: 1 }}>
          <Typography variant="h6">Volume + OBV Breakout Scanner</Typography>
          {readiness?.auto_run_after_eod && <Chip size="small" variant="outlined" label="Auto-runs after the EOD import" />}
          {lastRun ? (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip size="small" label={lastRun.status} color={CHIP_COLOR[lastRun.status] ?? "default"} />
              {lastRunWhen && (
                <Typography variant="caption" color="text.secondary">
                  Last run: {lastRunWhen}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No run yet
            </Typography>
          )}
        </Stack>

        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          Screens every stock for a close above its prior 20-session high confirmed by relative volume and OBV, using
          completed daily candles only. It reads price history already stored — it does <strong>not</strong> fetch
          from Breeze. So: <strong>(1)</strong> make sure prices are imported up to the latest completed session (the
          import cards above; this needs a valid Breeze session), then <strong>(2)</strong> run the scanner. It also
          runs automatically after each scheduled EOD import. Results appear under Investing Idea → Market
          Opportunities → Volume + OBV Breakout.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {readiness?.warnings.map((w) => (
          <Alert
            key={w.code}
            severity={w.severity === "error" ? "error" : w.severity === "warning" ? "warning" : "info"}
            sx={{ mb: 1.5 }}
          >
            {w.message}
          </Alert>
        ))}

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={2} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={starting === "latest" ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={() => void start("latest")}
            disabled={running || starting !== null}
          >
            Run scanner (latest session{readiness ? `: ${dateLabel(readiness.latest_completed_session)}` : ""})
          </Button>
          <Button onClick={() => { void loadReadiness(); void loadStatus(); }} disabled={readinessLoading}>
            Refresh
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={2}>
          <TextField
            size="small"
            type="date"
            label="Backfill from"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: readiness?.latest_completed_session }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="Backfill to"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: readiness?.latest_completed_session }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={starting === "range" ? <CircularProgress size={16} /> : <HistoryIcon />}
            onClick={() => void start("range")}
            disabled={running || starting !== null || rangeInvalid}
          >
            Backfill date range
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Recomputes every trading session in the range from stored prices (weekends/holidays are skipped; up to 260
          sessions). Safe to repeat — results are updated in place, never duplicated. Only one scan can run at a time.
        </Typography>

        {job && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Job status:
              </Typography>
              <Chip size="small" label={job.state} color={CHIP_COLOR[job.state] ?? "default"} />
              <Typography variant="caption" color="text.secondary">
                {job.as_of_dates.length === 1
                  ? `session ${dateLabel(job.as_of_dates[0])}`
                  : `${job.as_of_dates.length} sessions (${dateLabel(job.as_of_dates[0])} – ${dateLabel(
                      job.as_of_dates[job.as_of_dates.length - 1]
                    )})`}
              </Typography>
            </Stack>
            {job.state === "RUNNING" && (
              <>
                <LinearProgress variant={job.total_stocks > 0 ? "determinate" : "indeterminate"} value={progress} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {job.total_stocks > 0
                    ? `Processed ${job.processed_stocks} / ${job.total_stocks} stocks`
                    : "Loading stock universe…"}
                </Typography>
              </>
            )}
            {job.state === "COMPLETED" && job.summary && (
              <Typography variant="body2">
                Evaluated {job.summary.stocks_evaluated} stock(s) — {job.summary.inserted} new, {job.summary.updated}{" "}
                updated, {job.summary.deleted} removed. Outcome: {countsLabel(job.summary.status_counts)}.
                {job.summary.error_count ? ` ${job.summary.error_count} stock/session error(s).` : ""}
              </Typography>
            )}
            {job.state === "FAILED" && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {job.error ?? "The scan failed."}
              </Alert>
            )}
          </Paper>
        )}

        {readinessLoading && !readiness ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Checking data readiness…
            </Typography>
          </Stack>
        ) : (
          readiness && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <Stat label="Latest completed session" value={dateLabel(readiness.latest_completed_session)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Stat label="Stocks in universe" value={readiness.universe_stocks} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Stat label="With that session's candle" value={readiness.stocks_with_session_bar} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Stat
                    label={`With ≥ ${readiness.min_history_bars} candles`}
                    value={readiness.stocks_with_min_history}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Stat label="Ready to scan" value={readiness.stocks_scannable} hint="both of the above" />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Stat
                    label="Latest scanned session"
                    value={readiness.latest_scanned_date ? dateLabel(readiness.latest_scanned_date) : "never"}
                    hint={
                      readiness.latest_scanned_date
                        ? countsLabel(readiness.latest_scanned_status_counts)
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
              {lastRun && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                  Last run: {lastRun.status}
                  {lastSummary.stocks_evaluated != null ? ` — ${String(lastSummary.stocks_evaluated)} stock(s) evaluated` : ""}
                  {lastSummary.status_counts
                    ? `; outcome: ${countsLabel(lastSummary.status_counts as Record<string, number>)}`
                    : ""}
                  {lastRun.error_message ? ` — ${lastRun.error_message}` : ""}
                </Typography>
              )}
            </Paper>
          )
        )}
      </CardContent>
    </Card>
  );
}

export default VolumeObvScannerOperationsCard;
